<template>
  <div class="relative">
    <!-- Table Control -->
    <div class="flex justify-between items-center mb-5 flex-col lg:flex-row space-y-5 lg:space-y-0">
      <div
        class="
          flex
          justify-between
          w-full
          lg:w-auto
          sm:space-x-5
        "
      >
        <!-- @todo should work later -->
        <select
          v-model="perPage"
          class="
            w-20
            h-10
            relative
            flex
          "
        >
          <template
            v-for="(page, index) in pages"
          >
            <option
              :key="index"
              :value="page"
            >
              {{ page }}
            </option>
          </template>
        </select>

        <component
          :is="pagination"
          ref="vuetablePagination"
          :css="css.pagination"
          @vuetable-pagination:change-page="onChangePage"
        />
      </div>

      <div class="flex gap-5 items-center justify-between w-full lg:w-auto flex-wrap">
        <vuetable-pagination-info
          v-show="!loading"
          ref="paginationInfo"
          class="p-0 whitespace-nowrap lg:whitespace-normal"
        />

        <slot name="table-actions" />
      </div>
    </div>

    <!-- Loader -->
    <div
      v-if="!firstLoaded"
      class="
        absolute
        top-0
        left-0
        w-full
        h-full
        bg-white
        dark:bg-muted-700
        opacity-50
        z-10
        flex
        items-center
        justify-center
        rounded-sm
        transition-opacity
        duration-200
        delay-200
      "
    >
      <svg
        class="
          animate-spin
          -ml-1
          mr-3
          h-10
          w-10
          text-primary
        "
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>

    <!-- Skeleton -->
    <div
      v-if="isLoading"
      class="space-y-2 flex flex-col bg-muted-50 dark:bg-muted-800 px-3 py-4 overflow-x-scroll border-muted-300 dark:border-muted-600 border rounded"
    >
      <div class="space-x-2 flex border-b border-gray-200 pb-2 text-xs font-medium">
        <div
          v-for="(field) in $attrs.fields"
          :key="field.title"
          class="w-1/12 skeleton-header rounded-sm flex flex-1 capitalize"
          v-text="field.title"
        />
      </div>
      <div
        v-for="item in perPage"
        :key="item"
        class="space-x-2 flex border-b border-gray-200 pb-3"
      >
        <div
          v-for="(field, i) in $attrs.fields"
          :key="i"
          class="w-1/12 h-5 skeleton-header bg-gray-300 rounded-sm flex flex-1 animate-pulse"
        />
      </div>
    </div>

    <!-- Table -->
    <div
      v-show="!isLoading"
      class="overflow-x-auto text-sm table-container border-muted-300 dark:border-muted-600 border rounded"
    >
      <vuetable
        v-bind="{ ...$attrs }"
        ref="vuetable"
        :css="{ ...css.table, ...$attrs.css }"
        v-on="$listeners"
        @vuetable:pagination-data="onPaginationData"
        @vuetable:loading="isLoading = true"
        @vuetable:loaded="isLoading = false"
      >
        <template
          v-for="(index, name) in $scopedSlots"
          #[name]="data"
        >
          <slot
            :name="name"
            v-bind="data"
          />
        </template>
      </vuetable>
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2/src/components/Vuetable';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination';
import VuetablePaginationDropdown from 'vuetable-2/src/components/VuetablePaginationDropdown';
import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo';

import { formatCurrency, formatOverdue } from '@/utils/stringUtils';

import {
  tableConfig,
  paginationConfig,
  iconsConfig
} from '@/constants';

export default {
  name: 'TableComponent',
  components: {
    Vuetable,
    VuetablePaginationDropdown,
    VuetablePagination,
    VuetablePaginationInfo
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    // @temporary fix for pagination
    // perPage: this.$store.state.pageNumbers.dashboard.liens.perPage,
    pages: [20, 50, 100, 250],
    css: {
      table: tableConfig,
      pagination: paginationConfig,
      icons: iconsConfig
    },
    formatCurrency,
    formatOverdue,
    isLoading: false,
    formatPreviousStage: (value) => value ?? 'None',
    paginationComponent: VuetablePagination,
    paginationData: null,
    firstLoaded: false
  }),
  computed: {
    pagination: {
      get() {
        return this.paginationComponent;
      },
      set() {
        this.paginationComponent = window.innerWidth < 640 ? VuetablePaginationDropdown : VuetablePagination;
      }
    },
    // @temporary fix for pagination
    perPage: {
      get() {
        return this.$store.state.pageNumbers.dashboard.liens.perPage;
      },
      set(value) {
        this.$emit('table:perPageChange', value);
      }
    }
  },
  watch: {
    '$attrs.fields': {
      async handler(val) {
        try {
          if (val && this.$slots['table-actions']) {
            await this.$nextTick();
            this.$refs.vuetable.normalizeFields();
          }
        } catch (error) {
          console.error('[Fields watcher]', error);
        }
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.windowSizeListener);
    this.paginationComponent = window.innerWidth < 640 ? VuetablePaginationDropdown : VuetablePagination;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.windowSizeListener);
  },
  methods: {
    windowSizeListener() {
      this.paginationComponent = window.innerWidth < 640 ? VuetablePaginationDropdown : VuetablePagination;
      this.onPaginationData(this.paginationData);
    },
    // handlePageChange() {
    //   console.log('cja', this.perPage);
    //   this.$emit('table:perPageChange', this.perPage);

    //   // this.$store.commit('setPageNumber', {
    //   //   key: 'dashboard',
    //   //   tabName: 'liens',
    //   //   value: { perPage: this.per_page }
    //   // });

    //   // this.$refs.vuetable.setPaginationData({ perPage: this.perPage });
    // },
    onChangePage(page) {
      this.$emit('table:pageChange', page);
      this.$refs.vuetable.changePage(page);
    },
    formatNumber(value) {
      return value;
    },
    formatDate(value) {
      return value
        ? new Date(value).toLocaleDateString('en-US', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric'
        }).replace(',', '')
        : '';
    },
    allcap(value) {
      return value.toUpperCase();
    },
    // when the pagination data is available, set it to pagination component
    onPaginationData(paginationData) {
      this.firstLoaded = true;
      this.paginationData = paginationData;
      this.$refs.vuetablePagination.setPaginationData(paginationData);
      this.$refs.paginationInfo.setPaginationData(paginationData);
    },
    async refresh() {
      await this.$nextTick();
      this.$refs.vuetable.refresh();
    }
  }
};
</script>

<style lang="scss" scoped>
.table-container {
  :deep {
    td {
      @apply items-center align-middle;
    }

    th {
      @apply capitalize;
    }
  }
}

::-webkit-scrollbar {
  @apply bg-primary-100 w-3 h-1;
}

::-webkit-scrollbar-thumb {
  @apply bg-primary-400;
}

.skeleton-header {
  min-width: 150px;
}
</style>
